import { takeEvery, select, put, takeLatest } from 'redux-saga/effects';
import {
  validateGeoOnServer,
  submitImport,
  autoFillDescriptives,
  setImportName,
  appNotify,
  setImportRows,
  setImportGeoValidationResult,
  setImportDupCheckErrors,
  setImportLoadingOverlayState,
  setImportNameValidation,
  setImportRowsSkippedCount,
  setIsFromRejectedImports
} from 'store/actions';
import {
  userSelector, importTypeStateSelector, opportunityImportSelectedChannelSelector, postAcqTaskImportWorkstreamTypeSelector
} from 'store/selectors';
import { postWithToken, postWithTokenV2 } from 'api/network';
import { ValidateRows, MapValidationResultsToRows } from 'services/importValidationService';
import { Constants } from 'utilities/constants';


export function* importSaga() {
  yield takeEvery(validateGeoOnServer, function* ({ payload }) {
    const { importType } = payload;
    yield put(setImportLoadingOverlayState({ importType, importLoadingOverlayState: { isLoading: true, message: 'Validating GEO information on server...' } }));

    const importTypeState = yield select(importTypeStateSelector, importType);
    const { importRows } = importTypeState;
    const uniqueZipCodes = importRows.reduce((accumulator, current) => {
      if (!accumulator.some(zip => zip === current.zipCode)) {
        accumulator.push(current.zipCode);
      }
      return accumulator;
    }, []);

    const countyValidationRequest = importRows.map(current => ({ importItemId: current.referenceId, street: current.street, city: current.city, county: current.county, state: current.state, zipCode: current.zipCode, unit: current.unit }));
    const geoValidationRequest = importRows.map(current => ({ referenceId: current.referenceId, street: current.street, zipCode: current.zipCode, city: current.city, state: current.state }));

    const importValidationPayload = {
      zipcodeValidationDtos: uniqueZipCodes.map(zipCode => ({ zipCode: zipCode })),
      countyValidationDtos: countyValidationRequest,
      geoValidationDtos: geoValidationRequest
    }

    try {
      const url = importType === Constants.ImportType.PROPERTIES
        ? '/api/explore/import/property/validateGeo'
        : '/api/underwrite/import/opportunity/validateGeo'
      const response = yield postWithToken(url, importValidationPayload);
      const validationResult = yield response.json();
      console.log('validationResult', validationResult);
      const zipCodeValidationResults = validationResult.zipcodeValidationDtos;
      const countyValidationResults = validationResult.countyValidationDtos;
      const geoValidationResults = validationResult.geoValidationDtos;

      let updatedImportRows = [...importRows];
      let invalidZipCodes = [];
      let invalidCounties = [];
      let invalidGeo = [];

      zipCodeValidationResults.forEach(result => {
        if (!result.passedValidation) {
          invalidZipCodes.push(result.zipCode);
        }
      })

      countyValidationResults.forEach(result => {
        if (!result.passedValidation) {
          invalidCounties.push(result.importItemId);
        }
      })

      geoValidationResults.forEach(validationResult => {
        
        let index = updatedImportRows.findIndex(row => row.referenceId === validationResult.referenceId);
        if (index === -1) return;
        
        const { geoResultDto } = validationResult;
        if (!geoResultDto) return;

        if(validationResult.geoResultDto.propertyMasterResult) {
          validationResult.geoResultDto.propertyMasterResultBkp = { ...validationResult.geoResultDto.propertyMasterResult }
        }
        
        const newRow = { ...updatedImportRows[index] };
        if (geoResultDto.passedValidation) {
          newRow.latitude = geoResultDto.propertyMasterResult.latitude;
          newRow.longitude = geoResultDto.propertyMasterResult.longitude;
        } else {
          invalidGeo.push(validationResult.referenceId);
        }

        // newRow.propertyMasterResult = geoResultDto.propertyMasterResult;
        updatedImportRows[index] = newRow;

      });

      let passedValidation = true;

      if (invalidZipCodes.length > 0) {
        passedValidation = false;
        const notifyMessage = `Invalid zip code${invalidZipCodes.length > 1 ? 's' : ''} found`;
        yield put(appNotify(notifyMessage, 'warning'));
      }
      if (invalidCounties.length > 0) {
        passedValidation = false;
        const notifyMessage = `Invalid ${invalidZipCodes.length > 1 ? 'counties' : 'county'} found`;
        yield put(appNotify(notifyMessage, 'warning'));
      }
      if (invalidGeo.length > 0) {
        passedValidation = false;
        const notifyMessage = `Invalid or ambiguous latitude${invalidGeo.length > 1 ? 's' : ''}/longitude${invalidGeo.length > 1 ? 's' : ''} found`;
        yield put(appNotify(notifyMessage, 'warning'));
      }
      if (passedValidation) {
        const notifyMessage = `Import passed server validation!`;
        yield put(appNotify(notifyMessage, 'success'));
      }

      yield put([
        setImportGeoValidationResult({
          importType,
          importGeoValidationResult: { passedValidation, invalidZipCodes, invalidCounties, invalidGeo, geoValidationDtos: geoValidationResults }
        }),
        setImportRows({ importType, importRows: updatedImportRows }),
        setImportLoadingOverlayState({ importType, importLoadingOverlayState: { isLoading: false, message: null } })
      ]);
    } catch (error) {
      yield put(setImportLoadingOverlayState({ importType, importLoadingOverlayState: { isLoading: false, message: null } }));
      yield put(appNotify(error.message, 'error'));
    }
  });

  yield takeEvery(autoFillDescriptives, function* ({ payload }) {
    const { importType, fieldType } = payload;
    yield put(setImportLoadingOverlayState({ importType, importLoadingOverlayState: { isLoading: true, message: 'Autofilling...' } }));
    const importTypeState = yield select(importTypeStateSelector, importType);
    const { importRows } = importTypeState;
    // create dtos
    let autoFillDescriptiveDtos;
    if (fieldType === 'blanks') {
      autoFillDescriptiveDtos = importRows.reduce((accumulator, current) => {
        if (!current.city || !current.state || !current.beds || !current.baths || !current.sqft || !current.yearBuilt || !current.county) {
          accumulator.push({ importItemId: current.referenceId, street: current.street, zipCode: current.zipCode });
        }
        return accumulator;
      }, []);

      if (autoFillDescriptiveDtos.length <= 0) {
        yield put(setImportLoadingOverlayState({ importType, importLoadingOverlayState: { isLoading: false, message: null } }));
        yield put(appNotify('No import items need autofill', 'warning'));
        return;
      }
    } else {
      autoFillDescriptiveDtos = importRows.map(row => {
        const { referenceId, street, zipCode } = row;
        return {
          importItemId: referenceId,
          street,
          zipCode
        }
      });
    }

    try {
      const url = importType === Constants.ImportType.PROPERTIES
        ? '/api/explore/import/property/autoFill'
        : '/api/underwrite/import/opportunity/autoFill'

      const result = yield postWithTokenV2(url, autoFillDescriptiveDtos); 

      const updatedImportRows = [...importRows];

      function getValue(field, matchRow, responseRow) {
        const shouldOverrideUser = fieldType === 'all';
        let value = matchRow[field];
        if ((!value || shouldOverrideUser) && responseRow[field]) { // Update value with GUD/PropMaster value if user input is blank or Autofill All is selected, but only if GUD/PropMaster is not null
          value = responseRow[field].toString();
        }
        return value;
      }

      // update rows with auto fill results
      result.forEach(element => {
        let index = updatedImportRows.findIndex(row => row.referenceId === element.importItemId);
        if (index !== -1) {
          const newRow = { ...updatedImportRows[index] };
          newRow.city = getValue('city', newRow, element);
          newRow.state = getValue('state', newRow, element);
          newRow.beds = getValue('beds', newRow, element);
          newRow.baths = getValue('baths', newRow, element);
          newRow.sqft = getValue('sqft', newRow, element);
          newRow.yearBuilt = getValue('yearBuilt', newRow, element);
          newRow.county = getValue('county', newRow, element);
          updatedImportRows[index] = newRow;
          console.log(newRow)
        }
      });

      // revalidate the rows
      const importSelectedChannel = importType === Constants.ImportType.OPPORTUNITIES ? yield select(opportunityImportSelectedChannelSelector) : null;
      const importWorkstreamType = importType === Constants.ImportType.POSTACQTASKS ? yield select(postAcqTaskImportWorkstreamTypeSelector) : null;
      console.log(updatedImportRows)
      const importSource = importType === Constants.ImportType.POSTACQTASKS ? 'ReVUe' : Constants.ImportSource.ManualImport;
      const importRowsWithValidationResult = yield ValidateRows(updatedImportRows, importSelectedChannel, importWorkstreamType, importType, importSource);

      yield put([
        setImportLoadingOverlayState({ importType, importLoadingOverlayState: { isLoading: false, message: null } }),
        setImportRows({ importType, importRows: importRowsWithValidationResult })
      ]);
    }
    catch (error) {
      yield put(setImportLoadingOverlayState({ importType, importLoadingOverlayState: { isLoading: false, message: null } }));
      console.log(error);
      yield put(appNotify(error.message, 'error'));
    }
  });

  yield takeEvery(submitImport, function* ({ payload }) {
    const { importType, onSubmitImportSuccess } = payload;
    const loadingMessage = importType === Constants.ImportType.TITLES
      ? 'Importing Titles...'
      : 'Validating import...'
    yield put(setImportLoadingOverlayState({
      importType,
      importLoadingOverlayState: { isLoading: true, message: loadingMessage }
    }));
    const importTypeState = yield select(importTypeStateSelector, importType);

    let importPayload;
    let url;
    if (importType === Constants.ImportType.TITLES) {
      importPayload = importTypeState.importRows.map(el => {
        return {
          ...el,
          transactionopportunityid: parseInt(el['id']),
          delinquenttaxes: parseFloat(el['delinquentTaxes']),
          liens: parseFloat(el['liens']),
        }
      });
      url = '/api/underwrite/import/title';
    } else {
      const importUser = yield select(userSelector);
      if (importType === Constants.ImportType.OPPORTUNITIES) {
        const { importSelectedChannel, importRows, importName, importOpportunityStatus } = importTypeState;
        let cleanedImportRows = importRows.map(row => {
          let cleanedRow = {...row};
          if (cleanedRow.opportunitySource?.trim() === '') {
            cleanedRow.opportunitySource = null;
          }
          return cleanedRow;
        });
        importPayload = {
          source: 'Underwrite',
          channel: importSelectedChannel,
          importCount: importRows.length,
          sender: 'ReVUe',
          importedBy: importUser.fullName,
          name: importName,
          importProperties: cleanedImportRows   // create reference because we are going to clear out the id's for entityframework to set
        };
        if (importTypeState.isFromRejectedImports) { // If in rejected imports mode, grab rejectedImportId from importRow and send in request
          importPayload.rejectedImportId = importRows[0].rejectedImportId;
        }
        if (importSelectedChannel === Constants.Channel.OFFMARKET) { // Only pass the user selected importOpportunityStatus for Off-Market imports, other channels are set by backend
          importPayload.requestedOpportunityStatus = importOpportunityStatus;
        }
        url = '/api/underwrite/import/opportunity';
      }
      if (importType === Constants.ImportType.PROPERTIES) {
        const { importRows, importName } = importTypeState;
        importPayload = {
          importCount: importRows.length,
          sender: 'ReVUe',
          importedBy: importUser.fullName,
          name: importName,
          exploreImportProperties: importRows   // create reference because we are going to clear out the id's for entityframework to set
        };
        url = '/api/explore/import/property';
      }
      if (importType === Constants.ImportType.POSTACQTASKS) {
        const workstreamType = yield select(postAcqTaskImportWorkstreamTypeSelector);
        const { importRows, importName } = importTypeState;
        importPayload = {
          source: 'ReVUe',
          workstreamType,
          name: importName,
          postAcquisitionTasks: importRows
        };
        url = '/api/postAcquisition/import';
      }
    }

    try {
      if (!url || !importPayload) throw new Error(`importSaga.js - Invalid importType of ${importType} specified`)
      const response = yield postWithToken(url, importPayload);
      const validationResult = yield response.json();

      if (validationResult.passedValidation) {
        const actions = [
          setImportLoadingOverlayState({ importType, importLoadingOverlayState: { isLoading: false, message: null } }),
          setImportRows({ importType, importRows: [] }),   // clear out rows
          setImportDupCheckErrors({ importType, dupCheckErrors: [] }), // clear out dup check errors
          setImportRowsSkippedCount({ importType, importRowsSkippedCount: null })
        ]

        if (importType === Constants.ImportType.TITLES) {
          actions.push(appNotify(`Successfully Imported ${importPayload.length} Titles`, 'success'))
        } else {
          if (importType === Constants.ImportType.POSTACQTASKS) {
            actions.push(appNotify(`Successfully imported ${importPayload.postAcquisitionTasks.length} task${importPayload.postAcquisitionTasks.length > 1 ? 's' : ''}`, 'success', { persist: true }, { additionalAction: { type: 'viewOnWorkflow', route: Constants.Routes.POSTACQ_WORKFLOW, filterValue: [['importId', '=', validationResult.importId]] } }))
          }
          actions.push(setImportName({ importType, importName: "" }));
          actions.push(setImportNameValidation({ importType, importNameValidation: { valid: null, message: "" } }));
        }
        if (importTypeState.isFromRejectedImports) { // If in rejected imports mode, exit
          actions.push(setIsFromRejectedImports({ importType, isFromRejectedImports: false }));
        }
        yield put(actions);
        onSubmitImportSuccess && onSubmitImportSuccess();  // Navigate to ImportStatusTab
      }
      else {
        yield put(setImportLoadingOverlayState({ importType, importLoadingOverlayState: { isLoading: false, message: null } }));
        if (validationResult.errorMessage) {
          // Failed import validation, not even be able to proceed to dup check and validate import items.
          // Keep whatever is on import UI intact
          yield put(appNotify(validationResult.errorMessage, 'error'));
        }
        else if (validationResult.dupCheckErrors.length > 0) {
          // set dup check errors and display them on import UI
          const actions = [];
          console.log('validationResult.dupCheckErrors', validationResult.dupCheckErrors);
          if (importType !== Constants.ImportType.POSTACQTASKS) {
            const newImportGeoValidationDtos = importTypeState.importGeoValidationResult.geoValidationDtos.map(geoValidationDto => {
              const foundDupCheckError = validationResult.dupCheckErrors.find(err => err.referenceId === geoValidationDto.referenceId);
              if (!foundDupCheckError) return geoValidationDto;
              return {
                ...geoValidationDto,
                geoResultDto: {
                  ...geoValidationDto.geoResultDto,
                  propertyMasterResult: foundDupCheckError.propertyMasterResult
                }
              }
            });
            actions.push(setImportGeoValidationResult({
              importType,
              importGeoValidationResult: {
                ...importTypeState.importGeoValidationResult,
                geoValidationDtos: newImportGeoValidationDtos
              }
            }));
          }
          actions.push(setImportDupCheckErrors({ importType, dupCheckErrors: validationResult.dupCheckErrors }));
          yield put(actions);
        }
        else {
          // Some row validation failed
          // set validation error to rows and then update rows on UI with validation results
          const updatedImportRows = MapValidationResultsToRows(importTypeState.importRows, validationResult.importItemValidationResults);
          yield put(setImportRows({ importType, importRows: updatedImportRows }));
        }
      }
    } catch (error) {
      yield put(setImportLoadingOverlayState({ importType, importLoadingOverlayState: { isLoading: false, message: null } }));
      yield put(appNotify(error.message, 'error'));
    }
  });

  yield takeLatest(setImportName, function* ({ payload }) {
    const { importType, importName } = payload;
    if (importName?.length >= 3) {
      try {
        const url = importType === Constants.ImportType.PROPERTIES
          ? '/api/explore/import/property/validateImportName'
          : importType === Constants.ImportType.POSTACQTASKS
            ? '/api/postAcquisition/import/validateImportName'
            : '/api/underwrite/import/opportunity/validateImportName'
        const response = yield postWithToken(url, { ImportName: importName });
        const responseJson = yield response.json();
        yield put(setImportNameValidation({
          importType,
          importNameValidation: { valid: responseJson.passedValidation, isWarning: responseJson.isWarning, message: responseJson.message }
        }));
      } catch (error) {
        yield put(appNotify(error.message, 'error'));
      }
    } else if (importName?.length > 0) {
      yield put(setImportNameValidation({
        importType,
        importNameValidation: { valid: false, message: "Import name must have a minimum of 3 characters." }
      }));
    } else {
      yield put(setImportNameValidation({
        importType,
        importNameValidation: { valid: null, message: "" }
      }));
    }
  });
}
